/**
 * 配信予約作成ダイアログ。
 * 仕様書: docs/01_spec/spec_mail_system.md B-12.4
 */
import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Mail as MailIcon, Tag, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import { useMailContacts } from '@/hooks/useMailContacts';
import { useMailGroups } from '@/hooks/useMailGroups';
import { useScheduleMailSend } from '@/hooks/useMailSchedules';
import { useMails } from '@/hooks/useMailData';
import { useNotificationsSettings } from '@/hooks/useSettings';

import { mailRecipientCap, UNSUBSCRIBE_TAG, type MailScheduleTargetType } from '@goenhub/shared';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 起動元から渡されたメール ID（あれば固定表示・選択無効化） */
  prefillMailId?: string;
  /** 初期 targetType（キャンペーンボード起動時は 'addresses' 推奨） */
  initialTargetType?: MailScheduleTargetType;
  /** 初期宛先（フォーム回答者など）。targetType='addresses' と併用する想定 */
  initialAddresses?: string[];
  /** ダイアログ上部に表示する補足ヒント（任意） */
  contextHint?: string;
  /** 予約作成成功時のコールバック（scheduleId / recipientCount を受け取れる） */
  onScheduled?: (result: { scheduleId: string; recipientCount: number }) => void;
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const MIN_LEAD_MINUTES = 5;

function toLocalInput(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function defaultSendAt(): string {
  // 30 分後をデフォルトに
  const d = new Date(Date.now() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  return toLocalInput(d);
}

export function ScheduleFormDialog({
  open,
  onOpenChange,
  prefillMailId,
  initialTargetType,
  initialAddresses,
  contextHint,
  onScheduled,
}: Props) {
  const { data: mails } = useMails({ deleted: false, limit: 200 });
  const { data: contacts } = useMailContacts({ limit: 5000 });
  const { data: groups } = useMailGroups();
  const { data: mailAccounts } = useMailAccounts();
  const { data: notifSettings } = useNotificationsSettings();
  const scheduleMutation = useScheduleMailSend();
  const qc = useQueryClient();

  // 一括配信（大量送信）設定
  const crossDaySplitEnabled = notifSettings?.mailBulkCrossDaySplitEnabled === true;
  const relayAccountId = notifSettings?.mailBulkRelayAccountId ?? null;

  const activeAccounts = useMemo(
    () => (mailAccounts ?? []).filter((a) => a.status === 'active'),
    [mailAccounts],
  );
  const defaultAccountId = useMemo(() => {
    // SMTP リレー方式の既定が設定されていればそれを優先
    if (relayAccountId && activeAccounts.some((a) => a.id === relayAccountId)) {
      return relayAccountId;
    }
    return activeAccounts.find((a) => a.isDefault)?.id ?? activeAccounts[0]?.id ?? '';
  }, [activeAccounts, relayAccountId]);

  const [mailId, setMailId] = useState<string>('');
  const [fromAccountId, setFromAccountId] = useState<string>('');
  const [targetType, setTargetType] = useState<MailScheduleTargetType>('group');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [addressInput, setAddressInput] = useState('');
  const [cc, setCc] = useState<string[]>([]);
  const [sendAt, setSendAt] = useState<string>(defaultSendAt());

  // 既存の全タグ（contact から抽出）
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts ?? []) {
      for (const t of c.tags ?? []) if (t.trim()) set.add(t);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [contacts]);

  // 配信停止アドレス集合（contact ドキュメント単位ではなくアドレス単位で判定する）。
  // 同一アドレスが複数 contact に重複していても、1 件でも配信停止なら除外する。
  const unsubscribedAddresses = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts ?? []) {
      if (!(c.tags ?? []).includes(UNSUBSCRIBE_TAG)) continue;
      const def = c.defaultEmail?.trim().toLowerCase();
      if (def) set.add(def);
      for (const e of c.emails ?? []) {
        const a = e?.address?.trim().toLowerCase();
        if (a) set.add(a);
      }
    }
    return set;
  }, [contacts]);

  // 受信者プレビュー: { net = 実際に送られる件数, excluded = 配信停止で除外された件数 }
  // サーバ resolveRecipients と一致させる: group / tag / addresses すべてで配信停止を除外する。
  const recipientPreview = useMemo(() => {
    // アドレス集合に正規化してから配信停止を差し引く（サーバと同じ判定）。
    const summarize = (emails: Iterable<string>) => {
      const uniq = new Set<string>();
      for (const e of emails) {
        const a = e?.trim().toLowerCase();
        if (a) uniq.add(a);
      }
      let excluded = 0;
      for (const a of uniq) if (unsubscribedAddresses.has(a)) excluded++;
      return { net: uniq.size - excluded, excluded };
    };

    if (targetType === 'group') {
      if (!selectedGroupId) return { net: 0, excluded: 0 };
      const members = (contacts ?? []).filter((c) =>
        (c.groupIds ?? []).includes(selectedGroupId),
      );
      return summarize(members.map((c) => c.defaultEmail ?? ''));
    }
    if (targetType === 'tag') {
      if (selectedTags.length === 0) return { net: 0, excluded: 0 };
      const tagSet = new Set(selectedTags);
      const matched: string[] = [];
      for (const c of contacts ?? []) {
        if (!(c.tags ?? []).some((t) => tagSet.has(t))) continue;
        if (c.defaultEmail) matched.push(c.defaultEmail);
      }
      return summarize(matched);
    }
    if (targetType === 'addresses') {
      return summarize(addresses);
    }
    return { net: 0, excluded: 0 };
  }, [
    targetType,
    selectedGroupId,
    selectedTags,
    addresses,
    contacts,
    unsubscribedAddresses,
  ]);

  const previewCount = recipientPreview.net;
  const unsubscribedCount = recipientPreview.excluded;

  // 選択中の送信元アカウントと、その宛先数上限（Gmail 2000 / SMTP リレー高め）
  const selectedAccount = useMemo(
    () => activeAccounts.find((a) => a.id === fromAccountId),
    [activeAccounts, fromAccountId],
  );
  const recipientCap = useMemo(
    () => (selectedAccount ? mailRecipientCap(selectedAccount) : null),
    [selectedAccount],
  );
  const overCap = recipientCap != null && previewCount > recipientCap;
  // 日跨ぎ分割が有効なら上限超過でも送信可（翌日以降へ自動分割）
  const splitDays =
    overCap && recipientCap != null ? Math.ceil(previewCount / recipientCap) : 1;
  const blockOverCap = overCap && !crossDaySplitEnabled;

  // open 時のリセット。
  // defaultAccountId はここに含めない（送信元アカウントは非同期ロードで後から確定するため、
  // ここに含めると再実行され、初期宛先・送信日時などが巻き戻ってしまう）。
  useEffect(() => {
    if (open) {
      setMailId(prefillMailId ?? '');
      setTargetType(initialTargetType ?? 'group');
      setSelectedGroupId('');
      setSelectedTags([]);
      setTagInput('');
      setAddresses(initialAddresses ?? []);
      setAddressInput('');
      setCc([]);
      setSendAt(defaultSendAt());
      // モーダルを開くたびに mail_contacts / mail_groups を再同期する。
      // groupIds の逆引き（送信予定宛先のプレビュー）は差分同期キャッシュ経由で
      // ロードされるため、グループ作成/メンバー変更直後だと React Query のキャッシュが
      // 古いまま「0 件」と表示され、予約が弾かれる誤解を生む（ハード更新で直る症状）。
      // open 時に invalidate して、ページ再読込と同等の差分同期を軽量に走らせる。
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
    }
    // initialAddresses は下の専用 effect で追従（ここに入れると宛先ロードのたびに
    // 全項目がリセットされてしまうため、あえて依存から外す）。
  }, [open, prefillMailId, initialTargetType, qc]);

  // 送信元アドレスの既定反映（open 時 / アカウント非同期ロード後）。
  // 既定が後から確定しても、ここだけが追従するので他の入力は巻き戻らない。
  useEffect(() => {
    if (open) setFromAccountId(defaultAccountId);
  }, [open, defaultAccountId]);

  // 初期宛先（フォーム回答者など）の反映。
  // 起動元で非同期取得した宛先がダイアログ表示後に確定するケースに対応するため、
  // リセット effect とは分離して initialAddresses 変化時のみ上書きする。
  useEffect(() => {
    if (open && initialAddresses && initialAddresses.length > 0) {
      setAddresses(initialAddresses);
    }
  }, [open, initialAddresses]);

  const addTag = (raw: string) => {
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setSelectedTags((prev) => {
      const next = [...prev];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      return next;
    });
    setTagInput('');
  };

  const addAddress = (raw: string) => {
    const parts = raw
      .split(/[,、;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
    const valid = parts.filter((p) => EMAIL_RE.test(p));
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length > 0) {
      toast.error(`不正なメアド: ${invalid.join(', ')}`);
    }
    if (valid.length === 0) return;
    setAddresses((prev) => {
      const next = [...prev];
      for (const v of valid) if (!next.includes(v)) next.push(v);
      return next;
    });
    setAddressInput('');
  };

  const handleSubmit = async () => {
    if (!mailId) {
      toast.error('メールを選択してください');
      return;
    }
    if (!fromAccountId) {
      toast.error('送信元アドレスを選択してください（メール設定から追加できます）');
      return;
    }
    if (previewCount === 0) {
      toast.error('宛先が 0 件です。ターゲット指定を確認してください');
      return;
    }
    if (blockOverCap && recipientCap != null) {
      toast.error(
        `宛先 ${previewCount} 件が送信元アカウントの上限 ${recipientCap} 件を超えています。SMTP リレー（Amazon SES 等）のアカウントを選ぶか、設定>②通知 で日跨ぎ分割を有効にしてください。`,
      );
      return;
    }
    const sendAtMs = new Date(sendAt).getTime();
    if (Number.isNaN(sendAtMs)) {
      toast.error('送信日時が不正です');
      return;
    }
    if (sendAtMs < Date.now() + MIN_LEAD_MINUTES * 60 * 1000) {
      toast.error(`送信日時は現在から ${MIN_LEAD_MINUTES} 分以降を指定してください`);
      return;
    }

    let targetIds: string[] = [];
    if (targetType === 'group') {
      if (!selectedGroupId) {
        toast.error('グループを選択してください');
        return;
      }
      targetIds = [selectedGroupId];
    } else if (targetType === 'tag') {
      if (selectedTags.length === 0) {
        toast.error('タグを 1 件以上指定してください');
        return;
      }
      targetIds = selectedTags;
    } else {
      // 確定し忘れた入力欄も拾う
      const trailing = addressInput.trim();
      const finalAddrs =
        trailing && EMAIL_RE.test(trailing.toLowerCase()) && !addresses.includes(trailing.toLowerCase())
          ? [...addresses, trailing.toLowerCase()]
          : addresses;
      if (finalAddrs.length === 0) {
        toast.error('メアドを 1 件以上指定してください');
        return;
      }
      targetIds = finalAddrs;
    }

    try {
      const res = await scheduleMutation.mutateAsync({
        mailId,
        targetType,
        targetIds,
        fromAccountId,
        ...(cc.length > 0 ? { cc } : {}),
        // CTA URL は cta セクションの cta_url_placeholder を使う（Phase 22a）
        sendAt: new Date(sendAt).toISOString(),
      });
      toast.success(
        overCap && crossDaySplitEnabled
          ? `予約しました（${res.recipientCount} 件・${splitDays} 日に分割、初回 ${new Date(sendAt).toLocaleString('ja-JP', { hour12: false })}）`
          : `予約しました（${res.recipientCount} 件、${new Date(sendAt).toLocaleString('ja-JP', { hour12: false })}）`,
      );
      onScheduled?.({
        scheduleId: res.scheduleId,
        recipientCount: res.recipientCount,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(`予約失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const minSendAt = toLocalInput(new Date(Date.now() + MIN_LEAD_MINUTES * 60 * 1000));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>配信予約</DialogTitle>
          <DialogDescription>
            メール本文・宛先・送信時刻を指定して予約します。送信は予約時刻に自動実行されます（1 分粒度）。
          </DialogDescription>
        </DialogHeader>

        {contextHint && (
          <div className="rounded border border-[var(--accent-cyan)]/40 bg-[var(--accent-cyan-subtle)] px-3 py-2 text-[11px] text-[var(--accent-cyan)] leading-relaxed">
            {contextHint}
          </div>
        )}

        <div className="space-y-4">
          {/* メール選択 */}
          <div>
            <Label className="text-xs">メール（必須）</Label>
            {prefillMailId ? (
              <div className="px-3 py-2 rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] text-[13px] flex items-center gap-2">
                <MailIcon className="size-4 text-[var(--text-muted)]" />
                <span className="font-medium truncate">
                  {mails?.find((m) => m.id === prefillMailId)?.subject ?? '（メール読み込み中…）'}
                </span>
              </div>
            ) : (
              <Select value={mailId} onValueChange={setMailId}>
                <SelectTrigger>
                  <SelectValue placeholder="メールを選択" />
                </SelectTrigger>
                <SelectContent>
                  {(mails ?? [])
                    .filter((m) => m.status !== 'sent' || m.sections?.length)
                    .map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.subject || '(件名なし)'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* 送信元 */}
          <div>
            <Label className="text-xs">送信元アドレス</Label>
            {activeAccounts.length === 0 ? (
              <div className="px-3 py-2 rounded-sm border border-[var(--accent-red)] bg-[var(--accent-red-subtle)] text-[12px]">
                送信元アドレスが未登録です。
                <Link
                  to="/mail/settings"
                  className="ml-1 underline text-[var(--accent-red)]"
                  onClick={() => onOpenChange(false)}
                >
                  メール設定
                </Link>
                から追加してください。
              </div>
            ) : (
              <Select value={fromAccountId} onValueChange={setFromAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="送信元を選択" />
                </SelectTrigger>
                <SelectContent>
                  {activeAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.displayName} <span className="text-[var(--text-muted)]">&lt;{a.email}&gt;</span>
                      {a.isDefault ? ' ★既定' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* ターゲットタイプ */}
          <div>
            <Label className="text-xs">送信先タイプ</Label>
            <div className="flex gap-2">
              {(
                [
                  { value: 'group', label: 'グループ', icon: Users },
                  { value: 'tag', label: 'タグ', icon: Tag },
                  { value: 'addresses', label: '直接アドレス', icon: MailIcon },
                ] as const
              ).map((opt) => {
                const Icon = opt.icon;
                const active = targetType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTargetType(opt.value)}
                    className={
                      'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs border transition-colors ' +
                      (active
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                        : 'border-[var(--border)] hover:border-[var(--text-muted)] text-[var(--text-secondary)]')
                    }
                  >
                    <Icon className="size-3.5" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* グループ選択 */}
          {targetType === 'group' && (
            <div>
              <Label className="text-xs">グループ（必須）</Label>
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger>
                  <SelectValue placeholder="グループを選択" />
                </SelectTrigger>
                <SelectContent>
                  {(groups ?? []).map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}（{g.memberContactIds?.length ?? 0} 名 ・{' '}
                      {g.type === 'ai_keyword' ? 'AI' : '手動'}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* タグ選択 */}
          {targetType === 'tag' && (
            <div>
              <Label className="text-xs">タグ（複数可・OR 結合）</Label>
              <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
                {selectedTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => setSelectedTags((p) => p.filter((x) => x !== t))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={selectedTags.length === 0 ? 'タグを入力（Enter で確定）' : ''}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    const composing = e.nativeEvent.isComposing || e.keyCode === 229;
                    if (e.key === 'Enter' && !composing) {
                      e.preventDefault();
                      addTag(tagInput);
                    } else if (
                      e.key === 'Backspace' &&
                      tagInput === '' &&
                      selectedTags.length > 0 &&
                      !composing
                    ) {
                      setSelectedTags((p) => p.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    if (tagInput.trim()) addTag(tagInput);
                  }}
                  className="flex-1 min-w-[12ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                />
              </div>
              {allTags.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] text-[var(--text-muted)] mb-1">
                    既存タグから選択
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {allTags.map((t) => {
                      const sel = selectedTags.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setSelectedTags((p) =>
                              p.includes(t) ? p.filter((x) => x !== t) : [...p, t],
                            )
                          }
                          className={
                            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors ' +
                            (sel
                              ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
                              : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border-transparent hover:border-[var(--accent)] hover:text-[var(--accent)]')
                          }
                        >
                          {sel && <span className="mr-1">✓</span>}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 直接アドレス */}
          {targetType === 'addresses' && (
            <div>
              <Label className="text-xs">配信先メアド（複数可）</Label>
              <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
                {addresses.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                  >
                    {a}
                    <button
                      type="button"
                      onClick={() => setAddresses((p) => p.filter((x) => x !== a))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  placeholder={addresses.length === 0 ? 'メアドを入力（Enter / カンマ で確定）' : ''}
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAddress(addressInput);
                    } else if (
                      e.key === 'Backspace' &&
                      addressInput === '' &&
                      addresses.length > 0
                    ) {
                      setAddresses((p) => p.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    if (addressInput.trim()) addAddress(addressInput);
                  }}
                  className="flex-1 min-w-[20ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] font-mono"
                />
              </div>
            </div>
          )}

          {/* 受信者プレビュー（配信停止タグ付きは自動除外済みの正味件数を表示） */}
          <div className="text-xs text-[var(--text-secondary)] bg-[var(--bg-elevated)] rounded px-3 py-2 border border-[var(--border)]">
            送信予定宛先: <span className="font-bold text-[var(--accent)]">{previewCount}</span> 件
            {unsubscribedCount > 0 && (
              <span className="text-[var(--text-muted)]">
                {' '}（内 <span className="font-semibold">{unsubscribedCount}</span> 名 配信停止で除外）
              </span>
            )}
            {recipientCap != null && (
              <span className="text-[var(--text-muted)]">
                {' '}/ アカウント上限 {recipientCap} 件
              </span>
            )}
          </div>
          {overCap && recipientCap != null && crossDaySplitEnabled && (
            <div className="rounded border border-[var(--accent-cyan)]/40 bg-[var(--accent-cyan-subtle)] px-3 py-2 text-[11px] text-[var(--accent-cyan)] leading-relaxed">
              宛先 {previewCount} 件が 1 日上限 {recipientCap} 件を超えるため、
              <span className="font-bold">{splitDays} 日</span>に分割して送信されます
              （1 日目 {recipientCap} 件、以降同時刻に繰り越し）。
            </div>
          )}
          {blockOverCap && recipientCap != null && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-600 leading-relaxed">
              宛先 {previewCount} 件が送信元アカウントの上限 {recipientCap} 件を超えています。
              {selectedAccount?.provider === 'gmail_oauth'
                ? 'Gmail は 1 日の送信上限があるため、SMTP リレー（Amazon SES 等）のアカウントを選ぶか、設定>②通知 で日跨ぎ分割を有効にしてください。'
                : '設定>②通知 で日跨ぎ分割を有効にするか、アカウントの送信枠を引き上げてください。'}
            </div>
          )}

          {/* 送信控え（任意） */}
          <div>
            <Label className="text-xs">送信控え（任意・複数可）</Label>
            <EmailChipsInput
              value={cc}
              onChange={setCc}
              placeholder="控えを受け取るメアドを入力（Enter / カンマ で確定）"
            />
            {cc.length > 0 && (
              <p className="mt-1 text-[10px] text-[var(--text-muted)] leading-relaxed">
                配信内容と同じメールの控えが、指定アドレスへ 1 通だけ届きます（件名に【送信控え】が付きます）。宛先ごとの CC 付与はされません。
              </p>
            )}
          </div>

          {/* 送信日時 */}
          <div>
            <Label className="text-xs">
              送信日時（{MIN_LEAD_MINUTES} 分以降、現在 = {new Date().toLocaleString('ja-JP', { hour12: false })}）
            </Label>
            <div className="relative">
              <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
              <Input
                type="datetime-local"
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
                min={minSendAt}
                className="pl-9"
              />
            </div>
          </div>

          {/* CTA URL の上書き入力欄は廃止（Phase 22a / spec B-14）。
              A/B テスト等で URL を変えたい場合は「メール複製」してから CTA セクションを編集する設計。 */}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={
              scheduleMutation.isPending ||
              !mailId ||
              previewCount === 0 ||
              !fromAccountId ||
              blockOverCap
            }
          >
            {scheduleMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <CalendarClock className="size-4 mr-1" />
            )}
            予約する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
